import {
  DefaultApi,
  EventFormat,
  TransactionFormatTransactionShapeEnum,
} from '@digitalasset/json-api-ts-client/index';
import { useCallback } from 'react';
import { RowNew } from '../components/Table/types';
import { useLedgerApiClient } from '../contexts/LedgerApiContext';
import { useModuleParty } from '../contexts/ModuleContext';

interface TransferObject {
  sender: string;
  receiver: string;
  amount: string;
  instrumentId: { admin: string; id: string };
  requestedAt: string;
  executeBefore: string;
  inputHoldingCids: string[];
  meta: { values: Record<string, string> };
}

interface TransferArg {
  requestedAt?: string;
  executeBefore?: string;
  inputHoldingCids?: string[];
  meta?: { values: Record<string, string> };
}

interface ExercisedEvent {
  templateId?: string;
  interfaceId?: string;
  choice?: string;
  choiceArgument?: { transfer?: TransferArg };
  contractId?: string;
}

interface TransactionValue {
  events?: { ExercisedEvent?: ExercisedEvent }[];
}

interface UpdateResponse {
  update?: { Transaction?: { value?: TransactionValue } };
}

const TRANSFER_RULE_TEMPLATE_FRAGMENT = ':Utility.Registry.V0.Rule.Transfer:TransferRule';
const TRANSFER_INSTRUCTION_INTERFACE_FRAGMENT =
  ':Splice.Api.Token.TransferInstructionV1:TransferInstruction';

const REASON_META_KEY = 'splice.lfdecentralizedtrust.org/reason';

const TRANSFER_RULE_CHOICES = new Set([
  'TransferRule_DirectTransfer',
  'TransferRule_Transfer',
  'TransferRule_TwoStepTransfer',
]);

const REJECT_CHOICES = new Set(['TransferInstruction_Reject', 'TransferInstruction_Withdraw']);

const buildEventFormat = (party: string): EventFormat =>
  ({
    filtersByParty: { [party]: { cumulative: [] } },
    verbose: false,
  }) as unknown as EventFormat;

const buildTransferObject = (row: RowNew, transferArg?: TransferArg): TransferObject => ({
  sender: row.sender,
  receiver: row.receiver,
  amount: row.amount,
  instrumentId: { admin: row.registrar, id: row.instrumentId },
  requestedAt: transferArg?.requestedAt ?? row.createdAt,
  executeBefore: transferArg?.executeBefore ?? row.executeBefore ?? '',
  inputHoldingCids: [...(transferArg?.inputHoldingCids ?? row.inputHoldingCids ?? [])],
  meta: transferArg?.meta ?? { values: {} },
});

const interfaceRowTransferArg = (row: RowNew): TransferArg => ({
  requestedAt: row.createdAt,
  executeBefore: row.executeBefore ?? '',
  inputHoldingCids: row.inputHoldingCids ?? [],
  meta: { values: { [REASON_META_KEY]: row.reference ?? '' } },
});

const extractExercisedEvents = (transaction: TransactionValue): ExercisedEvent[] =>
  (transaction.events ?? [])
    .map((e) => e.ExercisedEvent)
    .filter((e): e is ExercisedEvent => Boolean(e));

const isTransferRuleEvent = (e: ExercisedEvent): boolean =>
  Boolean(e.templateId?.includes(TRANSFER_RULE_TEMPLATE_FRAGMENT)) &&
  TRANSFER_RULE_CHOICES.has(e.choice ?? '');

const isRejectEvent = (e: ExercisedEvent): boolean =>
  Boolean(e.interfaceId?.includes(TRANSFER_INSTRUCTION_INTERFACE_FRAGMENT)) &&
  REJECT_CHOICES.has(e.choice ?? '');

const fetchTransferArgFromLedger = async (
  api: DefaultApi,
  contractId: string,
  eventFormat: EventFormat,
): Promise<TransferArg> => {
  const createdResponse = await api.postV2EventsEventsByContractId({ contractId, eventFormat });
  const offset = createdResponse.created?.createdEvent?.offset;
  if (!offset) {
    throw new Error(`No created event found for contract ${contractId}`);
  }

  const updateResponse = (await api.postV2UpdatesUpdateByOffset({
    offset,
    updateFormat: {
      includeTransactions: {
        transactionShape: TransactionFormatTransactionShapeEnum.TRANSACTION_SHAPE_LEDGER_EFFECTS,
        eventFormat,
      },
    },
  })) as UpdateResponse;

  const transaction = updateResponse.update?.Transaction?.value;
  if (!transaction) {
    throw new Error(`No transaction found in update response at offset ${offset}`);
  }

  const exercisedEvents = extractExercisedEvents(transaction);
  const transferRuleEvent = exercisedEvents.find(isTransferRuleEvent);
  const rejectEvent = exercisedEvents.find(isRejectEvent);

  if (transferRuleEvent) {
    const transferArg = transferRuleEvent.choiceArgument?.transfer;
    if (!transferArg) {
      throw new Error(`TransferRule event for ${contractId} is missing its transfer argument`);
    }
    return transferArg;
  }
  if (rejectEvent?.contractId) {
    const originalResponse = await api.postV2EventsEventsByContractId({
      contractId: rejectEvent.contractId,
      eventFormat,
    });
    const createArg = originalResponse.created?.createdEvent?.createArgument as
      | { transfer?: TransferArg }
      | undefined;
    if (!createArg?.transfer) {
      throw new Error(
        `Original TransferInstruction ${rejectEvent.contractId} is missing its transfer argument`,
      );
    }
    return createArg.transfer;
  }
  throw new Error(`Could not extract full transfer data for row ${contractId}`);
};

export const useGetFullTransferObject = () => {
  const ledgerApi = useLedgerApiClient();
  const party = useModuleParty();

  return useCallback(
    async (row: RowNew): Promise<TransferObject> => {
      const api = ledgerApi.api() as DefaultApi;
      const eventFormat = buildEventFormat(party);

      const transferArg = row.isInterface
        ? interfaceRowTransferArg(row)
        : await fetchTransferArgFromLedger(api, row.contractId, eventFormat);

      return buildTransferObject(row, transferArg);
    },
    [ledgerApi, party],
  );
};
